import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { PlusCircle, Play, Pause, Trash2, Clock, CheckCircle, XCircle } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { toast } from 'sonner';

interface ScheduledJob {
  id: string;
  name: string;
  symbol: string;
  pages: number;
  interval_seconds: number;
  status: 'active' | 'paused' | 'disabled';
  start_at: string;
  next_run_at: string;
  last_run_at?: string;
  last_status?: string;
  run_count: number;
  success_count: number;
  failure_count: number;
  created_at: string;
}

const fetchScheduledJobs = async (): Promise<ScheduledJob[]> => {
  const { data, error } = await supabase
    .from('scheduled_crawl_jobs')
    .select('*')
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data || [];
};

const createScheduledJob = async (job: Omit<ScheduledJob, 'id' | 'created_at' | 'run_count' | 'success_count' | 'failure_count'>) => {
  const { data: { user } } = await supabase.auth.getUser();
  
  const { data, error } = await supabase
    .from('scheduled_crawl_jobs')
    .insert({
      ...job,
      created_by: user?.id,
    })
    .select()
    .single();

  if (error) throw error;
  return data;
};

const updateScheduledJob = async (id: string, updates: Partial<ScheduledJob>) => {
  const { data, error } = await supabase
    .from('scheduled_crawl_jobs')
    .update(updates)
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data;
};

const deleteScheduledJob = async (id: string) => {
  const { error } = await supabase
    .from('scheduled_crawl_jobs')
    .delete()
    .eq('id', id);

  if (error) throw error;
};

export const ScheduledJobsManagement = () => {
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingJob, setEditingJob] = useState<ScheduledJob | null>(null);

  const { data: scheduledJobs = [], isLoading } = useQuery({
    queryKey: ['scheduled-crawl-jobs'],
    queryFn: fetchScheduledJobs,
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  const createMutation = useMutation({
    mutationFn: createScheduledJob,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scheduled-crawl-jobs'] });
      setDialogOpen(false);
      setEditingJob(null);
      toast.success('Scheduled job created successfully');
    },
    onError: (error: Error) => {
      toast.error(`Failed to create scheduled job: ${error.message}`);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: Partial<ScheduledJob> }) =>
      updateScheduledJob(id, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scheduled-crawl-jobs'] });
      setDialogOpen(false);
      setEditingJob(null);
      toast.success('Scheduled job updated successfully');
    },
    onError: (error: Error) => {
      toast.error(`Failed to update scheduled job: ${error.message}`);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteScheduledJob,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scheduled-crawl-jobs'] });
      toast.success('Scheduled job deleted successfully');
    },
    onError: (error: Error) => {
      toast.error(`Failed to delete scheduled job: ${error.message}`);
    },
  });

  const handleSubmit = (formData: FormData) => {
    const name = formData.get('name') as string;
    const symbol = formData.get('symbol') as string;
    const pages = parseInt(formData.get('pages') as string);
    const interval_seconds = parseInt(formData.get('interval_seconds') as string);
    const status = formData.get('status') as 'active' | 'paused' | 'disabled';
    const start_at = formData.get('start_at') as string;

    const jobData = {
      name,
      symbol: symbol.toUpperCase(),
      pages,
      interval_seconds,
      status,
      start_at,
      next_run_at: start_at, // Will be calculated by trigger
    };

    if (editingJob) {
      updateMutation.mutate({ id: editingJob.id, updates: jobData });
    } else {
      createMutation.mutate(jobData);
    }
  };

  const handleStatusToggle = (job: ScheduledJob) => {
    const newStatus = job.status === 'active' ? 'paused' : 'active';
    updateMutation.mutate({
      id: job.id,
      updates: { status: newStatus }
    });
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <Badge className="bg-green-500"><Play className="h-3 w-3 mr-1" />Active</Badge>;
      case 'paused':
        return <Badge variant="outline"><Pause className="h-3 w-3 mr-1" />Paused</Badge>;
      case 'disabled':
        return <Badge variant="destructive">Disabled</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getLastStatusBadge = (status: string) => {
    switch (status) {
      case 'completed':
        return <Badge className="bg-green-500"><CheckCircle className="h-3 w-3 mr-1" />Success</Badge>;
      case 'failed':
        return <Badge variant="destructive"><XCircle className="h-3 w-3 mr-1" />Failed</Badge>;
      case 'pending':
      case 'running':
        return <Badge variant="outline"><Clock className="h-3 w-3 mr-1" />Running</Badge>;
      default:
        return status ? <Badge variant="outline">{status}</Badge> : null;
    }
  };

  const formatInterval = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };

  const intervalOptions = [
    { value: 60, label: '1 minute' },
    { value: 300, label: '5 minutes' },
    { value: 900, label: '15 minutes' },
    { value: 1800, label: '30 minutes' },
    { value: 3600, label: '1 hour' },
    { value: 7200, label: '2 hours' },
    { value: 14400, label: '4 hours' },
    { value: 21600, label: '6 hours' },
    { value: 43200, label: '12 hours' },
    { value: 86400, label: '24 hours' },
  ];

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <div>
              <CardTitle>Scheduled Crawl Jobs</CardTitle>
              <CardDescription>
                Manage automated TradingView data crawling schedules
              </CardDescription>
            </div>
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button onClick={() => setEditingJob(null)}>
                  <PlusCircle className="h-4 w-4 mr-2" />
                  Add Schedule
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[500px]">
                <form onSubmit={(e) => {
                  e.preventDefault();
                  const formData = new FormData(e.currentTarget);
                  handleSubmit(formData);
                }}>
                  <DialogHeader>
                    <DialogTitle>
                      {editingJob ? 'Edit' : 'Create'} Scheduled Job
                    </DialogTitle>
                    <DialogDescription>
                      Configure automated crawling for a specific symbol
                    </DialogDescription>
                  </DialogHeader>
                  
                  <div className="grid gap-4 py-4">
                    <div className="grid grid-cols-4 items-center gap-4">
                      <Label htmlFor="name" className="text-right">Name</Label>
                      <Input
                        id="name"
                        name="name"
                        defaultValue={editingJob?.name || ''}
                        placeholder="e.g., XAUUSD Hourly"
                        className="col-span-3"
                        required
                      />
                    </div>
                    
                    <div className="grid grid-cols-4 items-center gap-4">
                      <Label htmlFor="symbol" className="text-right">Symbol</Label>
                      <Input
                        id="symbol"
                        name="symbol"
                        defaultValue={editingJob?.symbol || ''}
                        placeholder="e.g., XAUUSD"
                        className="col-span-3"
                        required
                      />
                    </div>
                    
                    <div className="grid grid-cols-4 items-center gap-4">
                      <Label htmlFor="pages" className="text-right">Pages</Label>
                      <Input
                        id="pages"
                        name="pages"
                        type="number"
                        min="1"
                        max="10"
                        defaultValue={editingJob?.pages || 1}
                        className="col-span-3"
                        required
                      />
                    </div>
                    
                    <div className="grid grid-cols-4 items-center gap-4">
                      <Label htmlFor="interval_seconds" className="text-right">Interval</Label>
                      <Select name="interval_seconds" defaultValue={editingJob?.interval_seconds?.toString() || '3600'}>
                        <SelectTrigger className="col-span-3">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {intervalOptions.map(option => (
                            <SelectItem key={option.value} value={option.value.toString()}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="grid grid-cols-4 items-center gap-4">
                      <Label htmlFor="start_at" className="text-right">Start At</Label>
                      <Input
                        id="start_at"
                        name="start_at"
                        type="datetime-local"
                        defaultValue={editingJob?.start_at ? new Date(editingJob.start_at).toISOString().slice(0, 16) : new Date().toISOString().slice(0, 16)}
                        className="col-span-3"
                        required
                      />
                    </div>
                    
                    <div className="grid grid-cols-4 items-center gap-4">
                      <Label htmlFor="status" className="text-right">Status</Label>
                      <Select name="status" defaultValue={editingJob?.status || 'active'}>
                        <SelectTrigger className="col-span-3">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="active">Active</SelectItem>
                          <SelectItem value="paused">Paused</SelectItem>
                          <SelectItem value="disabled">Disabled</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  <DialogFooter>
                    <Button type="submit" disabled={createMutation.isPending || updateMutation.isPending}>
                      {editingJob ? 'Update' : 'Create'}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>
        
        <CardContent>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name/Symbol</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Interval</TableHead>
                  <TableHead>Next Run</TableHead>
                  <TableHead>Last Status</TableHead>
                  <TableHead>Stats</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={7} className="h-24 text-center">
                      Loading scheduled jobs...
                    </TableCell>
                  </TableRow>
                ) : scheduledJobs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="h-24 text-center">
                      No scheduled jobs found. Create one to start automated crawling.
                    </TableCell>
                  </TableRow>
                ) : (
                  scheduledJobs.map((job) => (
                    <TableRow key={job.id}>
                      <TableCell>
                        <div>
                          <div className="font-medium">{job.name}</div>
                          <div className="text-sm text-muted-foreground">{job.symbol} • {job.pages} pages</div>
                        </div>
                      </TableCell>
                      <TableCell>{getStatusBadge(job.status)}</TableCell>
                      <TableCell>{formatInterval(job.interval_seconds)}</TableCell>
                      <TableCell>
                        <div className="text-sm">
                          {formatDistanceToNow(new Date(job.next_run_at), { addSuffix: true })}
                        </div>
                      </TableCell>
                      <TableCell>
                        {job.last_status ? getLastStatusBadge(job.last_status) : '-'}
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">
                          <div>{job.run_count} runs</div>
                          <div className="text-muted-foreground">
                            {job.success_count}✓ {job.failure_count}✗
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleStatusToggle(job)}
                            disabled={updateMutation.isPending}
                          >
                            {job.status === 'active' ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setEditingJob(job);
                              setDialogOpen(true);
                            }}
                          >
                            Edit
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => deleteMutation.mutate(job.id)}
                            disabled={deleteMutation.isPending}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};